import { useState, useRef, useCallback } from "react";
import { useGo } from "@refinedev/core";
import {
  Upload,
  FileText,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  File,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { FormHeader, FormCard } from '@/components/forms';
import { cn } from "@/lib/utils";
import {
  ACCEPTED_FILE_EXTENSIONS,
  FILE_TYPE_LABELS,
  SUPPORTED_MIME_TYPES,
  type UploadResult,
} from "./types";

interface UploadingFile {
  file: File;
  status: "pending" | "uploading" | "success" | "error";
  progress: number;
  result?: UploadResult;
  error?: string;
}

/**
 * Форматирование размера файла
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) {return `${bytes} B`;}
  if (bytes < 1024 * 1024) {return `${(bytes / 1024).toFixed(1)} KB`;}
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Получить иконку по типу файла
 */
function getFileIcon(mimeType: string) {
  if (mimeType === "application/pdf") {
    return <FileText className="h-8 w-8 text-red-500" />;
  }
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel") || mimeType === "text/csv") {
    return <FileText className="h-8 w-8 text-green-500" />;
  }
  if (mimeType.includes("wordprocessing")) {
    return <FileText className="h-8 w-8 text-blue-500" />;
  }
  return <File className="h-8 w-8 text-muted-foreground" />;
}

/**
 * Knowledge Document upload page
 */
export function KnowledgeDocumentsCreate() {
  const go = useGo();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<UploadingFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFiles = useCallback((newFiles: FileList | File[]) => {
    const fileArray = [...newFiles];
    const validFiles = fileArray.filter((file) =>
      SUPPORTED_MIME_TYPES.includes(file.type as typeof SUPPORTED_MIME_TYPES[number])
    );

    if (validFiles.length !== fileArray.length) {
      const invalidCount = fileArray.length - validFiles.length;
      console.warn(`${invalidCount} файлов пропущено из-за неподдерживаемого формата`);
    }

    const uploadingFiles: UploadingFile[] = validFiles.map((file) => ({
      file,
      status: "pending",
      progress: 0,
    }));

    setFiles((prev) => [...prev, ...uploadingFiles]);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleFiles(e.target.files);
      }
    },
    [handleFiles]
  );

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const uploadFile = useCallback(
    async (index: number) => {
      setFiles((prev) =>
        prev.map((f, i) =>
          i === index ? { ...f, status: "uploading", progress: 10 } : f
        )
      );

      const file = files[index].file;
      const formData = new FormData();
      formData.append("file", file);

      try {
        // Глобальный интерцептор автоматически добавляет Authorization header
        const response = await fetch("/api/knowledge-base/upload", {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error?.message || "Ошибка загрузки");
        }

        const result = await response.json();

        setFiles((prev) =>
          prev.map((f, i) =>
            i === index
              ? { ...f, status: "success", progress: 100, result: result.data }
              : f
          )
        );
      } catch (error) {
        setFiles((prev) =>
          prev.map((f, i) =>
            i === index
              ? {
                  ...f,
                  status: "error",
                  progress: 0,
                  error: error instanceof Error ? error.message : "Ошибка загрузки",
                }
              : f
          )
        );
      }
    },
    [files]
  );

  const uploadAll = useCallback(async () => {
    const pendingIndexes = files
      .map((f, i) => (f.status === "pending" ? i : -1))
      .filter((i) => i !== -1);

    for (const index of pendingIndexes) {
      await uploadFile(index);
    }
  }, [files, uploadFile]);

  const pendingCount = files.filter((f) => f.status === "pending").length;
  const uploadingCount = files.filter((f) => f.status === "uploading").length;
  const successCount = files.filter((f) => f.status === "success").length;
  const errorCount = files.filter((f) => f.status === "error").length;

  return (
    <div className="space-y-6">
      <FormHeader
        title="Загрузка документов"
        description="Загрузите файлы для базы знаний AI"
        onBack={() => go({ to: "/knowledge-documents" })}
        sticky
      />

      <div className="grid gap-6 max-w-4xl">
        <FormCard
          title="Выберите файлы"
          description="Поддерживаемые форматы: PDF, DOCX, XLSX, XLS, CSV, TXT"
        >
          {/* Drop zone */}
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer",
              isDragOver
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-primary/50"
            )}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_FILE_EXTENSIONS}
              onChange={handleInputChange}
              className="hidden"
            />

            <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-1">
              Перетащите файлы сюда или нажмите для выбора
            </p>
            <p className="text-sm text-muted-foreground">
              Максимум 50 MB на файл
            </p>
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="mt-6 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">
                  Файлы ({files.length})
                </h4>
                {pendingCount > 0 && (
                  <Button
                    size="sm"
                    onClick={uploadAll}
                    disabled={uploadingCount > 0}
                  >
                    {uploadingCount > 0 ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Загрузка...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Загрузить все ({pendingCount})
                      </>
                    )}
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                {files.map((item, index) => (
                  <div
                    key={`${item.file.name}-${index}`}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-lg border",
                      item.status === "success" && "bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-900",
                      item.status === "error" && "bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-900",
                      item.status === "pending" && "bg-muted/50",
                      item.status === "uploading" && "bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-900"
                    )}
                  >
                    {getFileIcon(item.file.type)}

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {item.file.name}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{formatFileSize(item.file.size)}</span>
                        <span>•</span>
                        <span>
                          {FILE_TYPE_LABELS[item.file.type] || item.file.type}
                        </span>
                      </div>

                      {item.status === "uploading" && (
                        <Progress value={item.progress} className="mt-2 h-1" />
                      )}

                      {item.status === "error" && item.error && (
                        <p className="text-xs text-red-600 mt-1">{item.error}</p>
                      )}

                      {item.status === "success" && item.result && (
                        <p className="text-xs text-green-600 mt-1">
                          Документ добавлен в очередь обработки
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {item.status === "pending" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => uploadFile(index)}
                        >
                          <Upload className="h-4 w-4" />
                        </Button>
                      )}

                      {item.status === "uploading" && (
                        <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                      )}

                      {item.status === "success" && (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      )}

                      {item.status === "error" && (
                        <AlertCircle className="h-5 w-5 text-red-500" />
                      )}

                      {(item.status === "pending" || item.status === "error") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeFile(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              {(successCount > 0 || errorCount > 0) && (
                <div className="flex items-center gap-4 pt-4 text-sm">
                  {successCount > 0 && (
                    <span className="text-green-600">
                      Загружено: {successCount}
                    </span>
                  )}
                  {errorCount > 0 && (
                    <span className="text-red-600">
                      Ошибок: {errorCount}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </FormCard>

        {/* Info card */}
        <FormCard
          title="Как это работает"
          description="После загрузки файлы обрабатываются автоматически"
        >
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            <li>Загрузите файл (PDF, DOCX, Excel, CSV или TXT)</li>
            <li>Файл парсится и извлекается текст</li>
            <li>Текст разбивается на небольшие фрагменты (чанки)</li>
            <li>Для каждого чанка создаётся векторное представление</li>
            <li>AI ассистент сможет искать информацию в ваших документах</li>
          </ol>
        </FormCard>

        {/* Actions */}
        {successCount > 0 && (
          <div className="flex gap-3">
            <Button onClick={() => go({ to: "/knowledge-documents" })}>
              К списку документов
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setFiles([]);
                fileInputRef.current?.click();
              }}
            >
              Загрузить ещё
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
